import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Injector,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { isObject } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';

// match the css height for `.question-area.multilevel-menu`
const MAX_HEIGHT = 170;

const MENU_ITEM_HEIGHT = 24;
const SUBHEADER_HEIGHT = 17;

export interface MenuItem {
  label: string;
  value: string;
}

@Component({
  selector: 'h3-powerlist-menu',
  templateUrl: './powerlist-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: PowerlistMenuComponent,
    },
  ],
  standalone: false,
})
export class PowerlistMenuComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit, ControlValueAccessor
{
  @Input({ required: true }) menuItems!: MenuItem[];
  @Input() disabled = false;
  @ViewChild('powerlist', { static: true }) powerlist!: ElementRef;

  parsedMenuItems: any;

  private renderedMenu!: any;
  private selection?: string;
  private $el = $(this.elementRef.nativeElement);

  private onChange!: (_selection: string | undefined) => void;
  private onTouched!: () => void;
  private touched = false;

  constructor(
    private elementRef: ElementRef,
    injector: Injector,
  ) {
    super(injector);
  }

  // powerlistmenu.js manually copies flattened elements to a new document
  // fragment, which messes up normal Angular event listeners. So we intercept
  // all click events, and use classes to determine which method to call,
  // mirroring how this was done in Backbone.
  @HostListener('click', ['$event'])
  handleClickEvents(event: MouseEvent) {
    const $target = $(event.target!);

    if ($target.hasClass('header')) {
      this.onNavigateDown(event);
    } else if ($target.hasClass('breadcrumb')) {
      this.onNavigateUp(event);
    } else if ($target.hasClass('powerlist-item')) {
      this.onItemClicked(event);
    }
  }

  onNavigateDown(event: MouseEvent) {
    this.markAsTouched();

    const menu = $(event.target!).closest('.header');
    const menuNum = menu.attr('class')!.match(/header([0-9]+)/)![1];
    const ul = this.$el.find(`.submenu${menuNum} ul`);
    this.setHeight(ul);
  }

  onNavigateUp(event: MouseEvent) {
    this.markAsTouched();

    const menuNum = $(event.target!)
      .closest('.submenu')
      .attr('class')!
      .match(/submenu([0-9]+)/)![1];
    this.setHeight(this.$el.find(`.header${menuNum}`).closest('ul'));
  }

  onItemClicked(event: MouseEvent) {
    if (this.disabled) {
      return;
    }

    this.markAsTouched();

    event.stopPropagation();
    const li = $(event.target!);
    this.onItemSelected(li);
  }

  ngOnInit() {
    this.parsedMenuItems = this.parseSubMenus(this.menuItems);
  }

  ngAfterViewInit() {
    if (this.renderedMenu) {
      return;
    }
    this.renderMenu();
  }

  writeValue(selection: string) {
    this.setValue(selection);
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  isObject(value: any) {
    return isObject(value);
  }

  private renderMenu() {
    this.renderedMenu = new (window as any).Powerlistmenu({
      menuElement: this.powerlist.nativeElement,
      slideduration: 200,
      backarrow: '<span class="left glyphicon glyphicon-menu-left"></span>',
      rightarrow: '<span class="right glyphicon glyphicon-menu-right"></span>',
    });
    this.indicateValue();
  }

  private onItemSelected(li: any) {
    const selection = li.data('value').toString();
    this.setValue(selection);
    this.onChange(selection);
  }

  private setValue(selection: string) {
    this.selection = selection;
    if (this.renderedMenu) {
      this.indicateValue();
    }
  }

  private markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  private indicateValue() {
    this.$el.find('.selected').removeClass('selected');
    this.$el.find('textarea').val('');
    this.$el.find('.powerlist-free-text-characters').text('0');

    let li;
    if (this.selection) {
      li = this.$el.find(`[data-value="${this.selection}"]`);
    }

    let ul: any;
    if (li && li.length) {
      ul = li.closest('ul');
      li.addClass('selected');
      ul.addClass('selected');
    } else {
      this.selection = undefined;
      ul = this.$el.find('ul:first');
    }
    this.renderedMenu.selectMenu(ul);

    // setTimeout because jQuery automatically pads the height based on borders,
    // but that apparently doesn't work right away the first time we show the
    // menu
    setTimeout(() => {
      this.setHeight(ul);
    });
  }

  private setHeight(ul: any) {
    const items = ul.find('li');
    let height = items.length * MENU_ITEM_HEIGHT - 1;
    if (ul.parent().hasClass('submenu')) {
      height += SUBHEADER_HEIGHT;
    }
    height = Math.min(height, MAX_HEIGHT);
    this.$el.find('nav.powerlistmenu').height(height);
  }

  // Takes an array of {label, value} objects.
  // `label` can be pipe separated to put that item in a nested submenu.
  private parseSubMenus(items: any) {
    const subMenuOptions: any[] = [];
    items.forEach((menuItem: any) => {
      const subArray = menuItem.label.split('|');
      let currentMenu = subMenuOptions;
      subArray.forEach((subItem: any, i: any) => {
        if (i === subArray.length - 1) {
          currentMenu.push({ label: subItem, value: menuItem.value });
        } else {
          const subMenu = currentMenu.find(
            (item: any) =>
              item.label === subItem && typeof item.value !== 'string',
          );
          if (!subMenu) {
            currentMenu.push({ label: subItem, value: [] });
          }
          currentMenu = currentMenu.find(
            (currentItem: any) =>
              currentItem.label === subItem &&
              typeof currentItem.value !== 'string',
          ).value;
        }
      });
    });
    return subMenuOptions;
  }
}
